import { type FC, useState } from "react";
import { Platform, Pressable, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Icon } from "../../Icon";
import type { MediaCarouselNavDirection } from "../MediaCarousel.types";

export interface MediaCarouselNavButtonProps {
  readonly direction: MediaCarouselNavDirection;
  readonly onPress: () => void;
  readonly disabled: boolean;
  readonly accessibilityLabel: string;
}

const MediaCarouselNavButton: FC<MediaCarouselNavButtonProps> = ({
  direction,
  onPress,
  disabled,
  accessibilityLabel,
}) => {
  const { theme } = useUnistyles();
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => {
        setHovered(true);
      }}
      onHoverOut={() => {
        setHovered(false);
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      style={({ pressed }) => [
        styles.button,
        hovered && !disabled && styles.buttonHovered,
        pressed && !disabled && styles.buttonPressed,
        disabled && styles.buttonDisabled,
        Platform.OS === "web" && !disabled && styles.webInteractive,
      ]}
    >
      <Icon
        name={direction === "previous" ? "chevronLeft" : "chevronRight"}
        size={20}
        color={theme.colors.mediaOverlay.foreground}
      />
    </Pressable>
  );
};

MediaCarouselNavButton.displayName = "MediaCarouselNavButton";

export { MediaCarouselNavButton };

const BUTTON_SIZE = 36;

const styles = StyleSheet.create((theme) => ({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.mediaOverlay.backgroundLight,
  },
  buttonHovered: {
    backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
  },
  buttonPressed: {
    opacity: 0.8,
  },
  buttonDisabled: {
    opacity: 0.35,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out",
  }),
}));
